import { auth } from "./firebase.ts";
import { MOCK_MODE, mockApi } from "./mock.ts";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

export class ApiError extends Error {
  readonly status: number;
  constructor(code: string, status: number) {
    super(code);
    this.name = "ApiError";
    this.status = status;
  }
}

// Error codes the server returns from the admin-allowlist gate
// (expat-api/src/auth/admin.ts). Any 403 carrying one of these means
// the signed-in user is not an admin — App.tsx renders a full-page
// "no access" state on the first occurrence.
export const ADMIN_GATE_CODES: ReadonlySet<string> = new Set([
  "forbidden",
  "admin_email_unverified",
  "admin_disabled",
]);

type AdminGateListener = (code: string) => void;
const adminGateListeners = new Set<AdminGateListener>();

export function onAdminGateBlocked(fn: AdminGateListener): () => void {
  adminGateListeners.add(fn);
  return () => {
    adminGateListeners.delete(fn);
  };
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (MOCK_MODE) return mockApi<T>(path, options);
  const user = auth.currentUser;
  if (!user) throw new ApiError("not_signed_in", 401);
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.headers ?? {}),
      authorization: `Bearer ${token}`,
    },
  });
  const ct = res.headers.get("content-type") ?? "";
  const isJson = ct.includes("application/json");
  const body: unknown = isJson
    ? await res.json().catch(() => ({}))
    : await res.text().catch(() => "");
  if (!res.ok) {
    const code =
      typeof body === "object" && body !== null && "error" in body &&
        typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `http_${res.status}`;
    if (res.status === 403 && ADMIN_GATE_CODES.has(code)) {
      for (const fn of adminGateListeners) fn(code);
    }
    throw new ApiError(code, res.status);
  }
  return body as T;
}
